import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { Box, Button, Typography, Stack, IconButton } from "@mui/material";
import MDEditor, { EditorContext, ICommand, commands } from "@uiw/react-md-editor";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";

import { ImageUploader } from "./ImageUploader/ImageUploader";
import { useImageUploader } from "./ImageUploader/useImageUploader";

//* CUSTOM BUTTON
interface CustomButtonProps {
  isReset: boolean;
  onResetChange: (value: boolean) => void;
}

function CustomButton(props: CustomButtonProps) {
  const { isReset, onResetChange } = props;
  const { preview, dispatch } = useContext(EditorContext);

  const click = () => {
    dispatch!({
      preview: preview === "edit" ? "preview" : "edit",
    });
  };

  useEffect(() => {
    if (isReset) {
      dispatch!({ preview: "edit" });
      onResetChange(false);
    }
  }, [isReset, onResetChange, dispatch]);

  if (preview === "edit") {
    return (
      <Button onClick={click} className="flex justify-between items-center gap-1">
        <Typography className="text-xs">Preview</Typography>
        <RemoveRedEyeOutlinedIcon className="w-[16px]" />
      </Button>
    );
  }

  return (
    <Button onClick={click} className="flex justify-between items-center gap-1">
      <Typography className="text-xs">Write</Typography>
      <CreateOutlinedIcon className="w-[16px]" />
    </Button>
  );
}

export type SaveNoteData = {
  content: string;
  image: File | null;
  is_pinned: boolean;
};

interface CustomEditorNewProps {
  defaultValueText: string;
  onSaveNote: (note: SaveNoteData) => void;
  isLoading: boolean;
  isEdit?: boolean;
  onEditCancel?: () => void;
  imageUrl?: string;
  isNotePinned?: boolean;
  onDeleteBackendImage?: () => void;
}

export function CustomEditorNew(props: CustomEditorNewProps) {
  const {
    defaultValueText,
    onSaveNote,
    isLoading,
    isEdit,
    onEditCancel,
    imageUrl,
    isNotePinned = false,
    onDeleteBackendImage,
  } = props;
  const [newNote, setNewNote] = useState<string>(defaultValueText);
  const [isReset, setIsReset] = useState<boolean>(false);
  const [isPinned, setIsPinned] = useState(isNotePinned);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const {
    selectedImage,
    setSelectedImage,
    imageToAnnotate,
    setImageToAnnotate,
    annotatedImage,
    setAnnotatedImage,
    savedAnnotations,
    setSavedAnnotations,
    annotations,
    setAnnotations,
    clearImageUploader,
  } = useImageUploader();

  const isEmpty = newNote.trim().length === 0;

  const canCreate = Boolean(imageFile) || !isEmpty;

  const handleResetNote = () => {
    setIsReset(true);
    setNewNote("");
    setIsPinned(false);
    setImageFile(null);
    setHasImage(false);
    clearImageUploader();
  };

  const handleSaveNote = () => {
    onSaveNote({
      content: newNote,
      image: imageFile || null,
      is_pinned: isPinned,
    });
    handleResetNote();
  };

  const handleResetChange = (value: boolean) => {
    setIsReset(value);
  };

  const codePreview: ICommand = {
    name: "preview",
    keyCommand: "preview",
    value: "preview",
    icon: <CustomButton isReset={isReset} onResetChange={handleResetChange} />,
  };

  const defaultCommands = useMemo(
    () => [
      commands.bold,
      commands.italic,
      commands.strikethrough,
      commands.unorderedListCommand,
      commands.orderedListCommand,
    ],
    []
  );

  const [hasImage, setHasImage] = useState(imageUrl !== undefined);

  if (isLoading) return SkeletonIndicator({ height: 160, loading: true });

  return (
    <Stack
      gap="10px"
      sx={{
        width: "100%",
        background: "#E5E9ED",
        padding: "10px",
        borderRadius: "10px",
        marginBottom: "20px",
        mt: "8px",
        mb: "16px",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {!isEdit && (
          <Typography color="#49494D" fontSize={24}>
            New note:
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: "10px", alignItems: "center", ml: "auto" }}>
          <Typography color="black" fontSize={14}>
            Click to pin
          </Typography>

          <IconButton
            sx={{ width: "20px", height: "20px" }}
            onClick={() => {
              setIsPinned((previousIsPinned) => !previousIsPinned);
            }}
          >
            {isPinned ? (
              <PushPinIcon className="text-primary-bright" />
            ) : (
              <PushPinOutlinedIcon className="text-primary-light" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box display="grid" gridTemplateColumns="1fr 270px" gridTemplateRows="minmax(160px, auto) auto" gap="10px">
        <Box
          data-color-mode="light"
          className="flex flex-col gap-[3px] py-2.5 px-1.5 bg-white rounded"
          sx={{
            gridColumn: "1",
            gridRow: "1",
          }}
        >
          <Box
            sx={{
              // force line-through style for <del/> tags in the below markdown preview
              "& .w-md-editor-preview p del": {
                textDecoration: "line-through",
              },
            }}
          >
            <MDEditor
              className="shadow-none rounded-none"
              maxHeight={270}
              value={newNote}
              preview="edit"
              commands={defaultCommands}
              extraCommands={[codePreview]}
              onChange={(val) => {
                setNewNote(val!);
              }}
              textareaProps={{
                placeholder: "Add notes here to help us understand the context of the query.",
              }}
              style={{ whiteSpace: "pre-wrap" }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            gridColumn: "2",
            gridRow: "1",
          }}
        >
          <ImageUploader
            originalImage={imageUrl}
            hasImage={hasImage}
            onSaveAnnotatedImage={(file) => {
              setImageFile(file);
              setHasImage(file !== null);
            }}
            onDeleteBackendImage={onDeleteBackendImage}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            imageToAnnotate={imageToAnnotate}
            setImageToAnnotate={setImageToAnnotate}
            annotatedImage={annotatedImage}
            setAnnotatedImage={setAnnotatedImage}
            savedAnnotations={savedAnnotations}
            setSavedAnnotations={setSavedAnnotations}
            annotations={annotations}
            setAnnotations={setAnnotations}
          />
        </Box>

        <Box
          sx={{
            gridColumn: "1",
            gridRow: "2",
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          {isEdit && onEditCancel && (
            <GroundlightButton data-testid="cancel-edit-note-button" onClick={onEditCancel} variant="plain">
              Cancel
            </GroundlightButton>
          )}
        </Box>

        <Box
          sx={{
            gridColumn: "2",
            gridRow: "2",
          }}
        >
          <GroundlightButton
            sx={{
              width: "100%",
            }}
            onClick={handleSaveNote}
            disabled={!canCreate}
          >
            {isLoading ? "Loading..." : "Save"}
          </GroundlightButton>
        </Box>
      </Box>
    </Stack>
  );
}
